import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useStorageUpload } from '@/db/useStorageUpload';
import { readImageDimensions } from '@/util/imageDimensions';
import logger from '@/util/logger';
import { createId } from '@rock/db-admin';
import type { EquipmentTestLibraryEntryId, ImageOption } from '@rock/types';
import { ImageOff, Loader2 } from 'lucide-react';
import { type FC, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImageSearchDialog } from './ImageSearchDialog';
import { defaultImageQuery } from './testLibraryApi';

const log = logger('ImageSlotField');

/**
 * One photo on an entry — the product shot or the nameplate shot.
 *
 * Two ways to fill it, per slot rather than per entry: you often have your own photo of the unit but
 * need to search for a legible plate, or vice versa. An uploaded file goes straight to Storage; a
 * searched image is stored as the search result's URL and the `rehostTestLibraryImages` trigger pulls
 * it onto our bucket a moment later.
 */
export const ImageSlotField: FC<{
    slot: 'product' | 'nameplate';
    label: string;
    entryId: EquipmentTestLibraryEntryId;
    typeTitle: string;
    brand?: string;
    image: ImageOption | undefined;
    /** `origin` says how the photo arrived, which is what decides the entry's `source`. */
    onChange: (image: ImageOption | undefined, origin?: 'upload' | 'search') => void;
}> = ({ slot, label, entryId, typeTitle, brand, image, onChange }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const { uploadFile } = useStorageUpload();

    const handleFile = async (file: File) => {
        setIsUploading(true);
        try {
            const url = await uploadFile(file, `equipment-test-library/${entryId}/${slot}-${file.name}`);
            const dimensions = await readImageDimensions(file);
            onChange(
                {
                    id: createId(),
                    url,
                    // Our own upload is already on Storage, so it needs no rehost and no derived
                    // rendition — the full-size image doubles as its own thumbnail.
                    thumbnail: url,
                    ...dimensions,
                },
                'upload',
            );
        } catch (err) {
            log.error('Failed to upload test library image', { err });
            toast.error('Upload failed', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-2">
            <Label>{label}</Label>
            <div className="flex h-48 items-center justify-center overflow-hidden rounded border bg-slate-50">
                {image ? (
                    <img src={image.url} alt={label} className="h-full w-full object-contain" />
                ) : (
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                        <ImageOff className="h-6 w-6" />
                        <span className="text-xs">No photo yet</span>
                    </div>
                )}
            </div>
            <div className="flex flex-wrap gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                >
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Upload'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSearchOpen(true)}
                    disabled={isUploading}
                >
                    Search
                </Button>
                {image && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onChange(undefined)}>
                        Remove
                    </Button>
                )}
            </div>
            <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void handleFile(file);
                }}
            />
            <ImageSearchDialog
                open={searchOpen}
                onOpenChange={setSearchOpen}
                title={`Search for a ${label.toLowerCase()}`}
                defaultQuery={defaultImageQuery(slot, typeTitle, brand)}
                onSelect={(selected) => onChange(selected, 'search')}
            />
        </div>
    );
};
